import { getGlowImageUrl, TokenProgram } from "@glow/shared";
import classNames from "classnames";
import React from "react";

const defaultColors = [
  "var(--purple-50)",
  "var(--blue-50)",
  "var(--orange-50)",
  "var(--red-50)",
  "var(--cranberry-50)",
  "var(--green-50)",
  "var(--barney-50)",
];

// Note: This is a JS version of the Swift version located at
// glow-ios/Shared/Views/Wallet/CoinIcon.swift
// We want to keep this functionality in sync between web, iOS and Android
// so coins with no icons all have consistent colors
const strHash = (str: string) => {
  let result = 5381;
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  for (let x = 0; x < bytes.length; x++) {
    result = 127 * (result & 0x00ffff) + bytes[x];
  }
  return result;
};

const getDefaultColor = (address: string) => {
  const idx = strHash(address) % defaultColors.length;
  return defaultColors[idx];
};

type Rem = { rem: number };
const PX_PER_REM = 16;

export const CoinIcon = ({
  coin,
  size = 40,
  className,
}: {
  coin: TokenProgram.CoinInfo;
  size?: number | Rem;
  className?: string;
}) => {
  const cssSize = typeof size === "number" ? size : size.rem + "rem";
  const pixelSize = typeof size === "number" ? size : PX_PER_REM * size.rem;

  return (
    <div
      className={classNames("coin-icon flex-center-center", className)}
      style={{ width: cssSize, height: cssSize, fontSize: cssSize }}
    >
      {coin.icon_url ? (
        <div
          className="icon-inner"
          style={{
            backgroundImage: `url(${getGlowImageUrl({
              url: coin.icon_url,
              width: pixelSize,
              height: pixelSize,
            })})`,
          }}
        />
      ) : (
        <div
          className="initials-icon flex-center-center"
          style={{ backgroundColor: getDefaultColor(coin.mint_address) }}
        >
          <span className="symbol text-white fw-medium">
            {(coin.symbol || coin.mint_address).slice(0, 3).toUpperCase()}
          </span>
        </div>
      )}

      <style jsx>{`
        .coin-icon {
          background-color: var(--tertiary-bg-color);
          overflow: hidden;
          border-radius: 50%;

          .icon-inner {
            width: 100%;
            height: 100%;
            background-position: center center;
            background-repeat: no-repeat;
            background-size: cover;
          }

          .default-icon :global(svg path) {
            fill: var(--primary-bg-color);
            stroke: var(--primary-bg-color);
          }

          .initials-icon {
            width: 100%;
            height: 100%;
          }

          .symbol {
            /* We defined the font-size of the entire icon to be the width/height of the icon.
              Because of that, we can use em to size the text relative to the icon’s size.
              There’s nothing magic about this number, it’s just what looked proportional. */
            font-size: 0.32em;
          }
        }
      `}</style>
    </div>
  );
};
